import { useEffect, useState } from 'react';
import { PHASES_OF_PLAY } from '@4cm/core/phaseOfPlay';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import type { DrillSpec } from '@4cm/core/drills/types';
import { supabase } from '../supabase';
import type { LoadState } from '../loadState';

export type DrillSourceType = 'PLATFORM' | 'LICENSED' | 'CLUB' | 'AI_GENERATED' | 'CURATED_EXPERT';

export interface SessionDrill {
  sort_order: number;
  drill: {
    id: string;
    name: string | null;
    drill_type: string | null;
    primary_phase: PhaseOfPlay | null;
    sub_themes: string[];
    curator_name: string | null;
    curator_credentials: string | null;
    source_type: DrillSourceType | null;
    duration_mins: number | null;
    description: string | null;
    drill_spec: DrillSpec | null;
  } | null;
}

interface DrillRow {
  id: string;
  name: string | null;
  drill_type: string | null;
  primary_phase: string | null;
  sub_themes: string[] | null;
  curator_name: string | null;
  curator_credentials: string | null;
  source_type: string | null;
  duration_mins: number | null;
  description: string | null;
  drill_spec: DrillSpec | null;
}

interface SessionDrillRow {
  sort_order: number | null;
  drill: DrillRow | DrillRow[] | null;
}

const DRILL_SOURCE_TYPES: readonly DrillSourceType[] = [
  'PLATFORM',
  'LICENSED',
  'CLUB',
  'AI_GENERATED',
  'CURATED_EXPERT',
];

interface SessionDrillsResult {
  drills: SessionDrill[];
  state: LoadState;
  errorMessage?: string;
  /** @deprecated alias kept while consumers migrate to `state`. */
  loading: boolean;
}

export function useSessionDrills(sessionId: string | undefined): SessionDrillsResult {
  const [drills, setDrills] = useState<SessionDrill[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!sessionId) {
      setDrills([]);
      setState('empty');
      setErrorMessage(undefined);
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('session_drills')
          .select('sort_order, drill:drills(id, name, drill_type, primary_phase, sub_themes, curator_name, curator_credentials, source_type, duration_mins, description, drill_spec)')
          .eq('session_id', sessionId)
          .order('sort_order', { ascending: true });
        if (cancelled) return;
        if (error) {
          setDrills([]);
          setState('error');
          setErrorMessage(error.message);
          return;
        }
        const rows = (data ?? []) as SessionDrillRow[];
        const mapped = rows.map(toSessionDrill);
        setDrills(mapped);
        setState(mapped.length > 0 ? 'ok' : 'empty');
      } catch (err) {
        if (cancelled) return;
        // Offline / placeholder Supabase — surface state='error' so the
        // page can render an explicit message instead of a silent zero.
        console.warn('[useSessionDrills] load failed', err);
        setDrills([]);
        setState('error');
        setErrorMessage(err instanceof Error ? err.message : 'Could not load drills.');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return { drills, state, errorMessage, loading: state === 'loading' };
}

function toSessionDrill(row: SessionDrillRow): SessionDrill {
  const drill = Array.isArray(row.drill) ? row.drill[0] ?? null : row.drill;
  return {
    sort_order: row.sort_order ?? 0,
    drill: drill ? {
      id: drill.id,
      name: drill.name,
      drill_type: drill.drill_type,
      primary_phase: toPhaseOfPlay(drill.primary_phase),
      sub_themes: drill.sub_themes ?? [],
      curator_name: drill.curator_name,
      curator_credentials: drill.curator_credentials,
      source_type: toDrillSourceType(drill.source_type),
      duration_mins: drill.duration_mins,
      description: drill.description,
      drill_spec: drill.drill_spec ?? null,
    } : null,
  };
}

function toPhaseOfPlay(value: string | null): PhaseOfPlay | null {
  return PHASES_OF_PLAY.includes(value as PhaseOfPlay) ? value as PhaseOfPlay : null;
}

function toDrillSourceType(value: string | null): DrillSourceType | null {
  return DRILL_SOURCE_TYPES.includes(value as DrillSourceType) ? value as DrillSourceType : null;
}
